import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  ScaleIcon,
  UserIcon,
  ArrowRightIcon,
  EnvelopeIcon,
  LockClosedIcon,
  GlobeAltIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../../../contexts/AuthContext";
import {
  supabase,
  createOnboardingProgress,
  updateProfile,
  getProfile,
  createProfile,
} from "@/shared/db";
import { toast } from "sonner";
import { Button, Input } from "@/shared/ui";
import { ROUTES, isValidEmail } from "@/shared/core";
import {
  getCountries,
  isCountryEnabled,
  type CountryConfig,
} from "@/shared/config";
import type { UserType } from "../../../shared/types/common.types";
import type { UserTypeOption } from "../types/onboarding.types";

export const UserTypeSelectionPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const {
    user,
    refreshProfile,
    loading: authLoading,
    profile,
    isOnboardingCompleted,
  } = useAuth();
  const [step, setStep] = useState<"select-type" | "register">("select-type");
  const [selectedType, setSelectedType] = useState<UserType | null>(null);
  const [loading, setLoading] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] =
    useState(false);
  const [error, setError] = useState<string>("");

  const [registerData, setRegisterData] = useState({
    fullName: "",
    email: "",
    password: "",
    confirmPassword: "",
    country: "",
    acceptedTerms: false,
  });

  const [countries, setCountries] = useState<CountryConfig[]>([]);
  const [countriesLoading, setCountriesLoading] = useState(true);

  // Load countries from database
  useEffect(() => {
    const loadCountries = async () => {
      try {
        setCountriesLoading(true);
        const list = await getCountries();
        setCountries(list);
      } catch (err) {
        console.error("Error loading countries:", err);
        toast.error(t("errors.generic"));
      } finally {
        setCountriesLoading(false);
      }
    };
    loadCountries();
  }, []);

  // Check if user is already logged in
  React.useEffect(() => {
    if (!authLoading && user) {
      // If user exists but no type selected, show type selection
      if (!profile?.user_type) {
        setStep("select-type");
      }
    } else if (!authLoading && !user) {
      // If no user, start with type selection
      setStep("select-type");
    }
  }, [user, authLoading, profile]);

  // Redirect if onboarding is already completed
  React.useEffect(() => {
    if (!authLoading && user && profile && isOnboardingCompleted()) {
      const savedPath = sessionStorage.getItem("redirectAfterOnboarding");
      const userType = profile?.user_type;
      // Lawyers: use /dashboard/lawyer or lawyer subpath; never /dashboard (documents)
      const targetPath =
        userType === "lawyer"
          ? savedPath?.startsWith("/dashboard/lawyer")
            ? savedPath
            : "/dashboard/lawyer"
          : savedPath || ROUTES.DASHBOARD;
      sessionStorage.removeItem("redirectAfterOnboarding");
      navigate(targetPath);
    }
  }, [authLoading, user, profile, isOnboardingCompleted, navigate]);

  // Preserve redirect path when component mounts
  // This ensures that if user reloads on this page, we try to preserve any saved path
  React.useEffect(() => {
    const savedPath = sessionStorage.getItem("redirectAfterOnboarding");

    // If there's no saved path and user came from a protected route (via referrer)
    // Try to save it from the referrer
    if (!savedPath) {
      const referrer = document.referrer;
      if (
        referrer &&
        !referrer.includes("/onboarding") &&
        !referrer.includes("/auth")
      ) {
        try {
          const referrerUrl = new URL(referrer);
          const referrerPath = referrerUrl.pathname + referrerUrl.search;
          // Only save if it's a valid app route
          if (
            referrerPath.startsWith("/app") ||
            referrerPath.startsWith("/dashboard") ||
            referrerPath.startsWith("/settings")
          ) {
            sessionStorage.setItem("redirectAfterOnboarding", referrerPath);
          }
        } catch {
          // Invalid URL, ignore
        }
      }
    }
  }, []);

  const userTypes: UserTypeOption[] = [
    {
      type: "lawyer",
      title: t("onboarding.userType.lawyer.title"),
      description: t("onboarding.userType.lawyer.description"),
      icon: ScaleIcon,
      color: "bg-linear-to-br from-tiber-500 to-tiber-700",
      features: [
        t("onboarding.userType.lawyer.features.0"),
        t("onboarding.userType.lawyer.features.1"),
        t("onboarding.userType.lawyer.features.2"),
      ],
    },
  ];

  const handleTypeSelection = () => {
    if (!selectedType) {
      toast.error(t("onboarding.register.errors.selectUserType"));
      return;
    }
    // Move to registration step with selected type
    setStep("register");
  };

  const handleRegister = async () => {
    if (!selectedType) {
      setError(t("onboarding.register.errors.selectUserTypeFirst"));
      return;
    }

    setError("");

    // Validations
    if (!registerData.fullName || registerData.fullName.length < 3) {
      setError(t("onboarding.register.errors.nameMinLength"));
      return;
    }

    if (
      !registerData.email ||
      !isValidEmail(registerData.email)
    ) {
      setError(t("onboarding.register.errors.emailInvalid"));
      return;
    }

    if (!registerData.password || registerData.password.length < 8) {
      setError(t("onboarding.register.errors.passwordMinLength"));
      return;
    }

    if (!/[!@#$%^&*(),.?":{}|<>]/.test(registerData.password)) {
      setError(t("onboarding.register.errors.passwordSymbol"));
      return;
    }

    if (registerData.password !== registerData.confirmPassword) {
      setError(t("onboarding.register.errors.passwordsNotMatch"));
      return;
    }

    if (!registerData.country) {
      setError(t("onboarding.register.errors.countryRequired"));
      return;
    }

    // Validate that the selected country is enabled
    if (!(await isCountryEnabled(registerData.country))) {
      setError(
        t(
          "onboarding.register.errors.countryNotEnabled",
          "El país seleccionado no está disponible actualmente",
        ),
      );
      return;
    }

    // Validate terms acceptance
    if (!registerData.acceptedTerms) {
      setError(
        t(
          "onboarding.register.errors.termsRequired",
          "Debes aceptar los términos y condiciones",
        ),
      );
      return;
    }

    setLoading(true);

    try {
      // 1. Crear cuenta en Supabase Auth
      const { data: signUpData, error: signUpError } =
        await supabase.auth.signUp({
          email: registerData.email,
          password: registerData.password,
          options: {
            emailRedirectTo: `${window.location.origin}/auth/verify?type=${selectedType}`,
            data: {
              full_name: registerData.fullName,
              user_type: selectedType,
              country: registerData.country,
            },
          },
        });

      if (signUpError) {
        throw signUpError;
      }

      // Si Supabase requiere verificación de email, no hay sesión → redirigir a pantalla de verificación
      if (!signUpData?.session && signUpData?.user) {
        toast.success(t("auth.emailVerification.description"));
        navigate(
          `/auth/verify?email=${encodeURIComponent(registerData.email)}&from=signup`
        );
        return;
      }

      // 2. Obtener el usuario del resultado de signUp
      let newUser = signUpData?.user;

      // Si no hay usuario en el resultado, intentar obtenerlo de la sesión
      if (!newUser) {
        // Esperar un momento para que la sesión se establezca
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Intentar obtener de la sesión actual
        const {
          data: { session },
        } = await supabase.auth.getSession();
        newUser = session?.user ?? null;

        // Si aún no hay usuario, intentar getUser
        if (!newUser) {
          const {
            data: { user: authUser },
          } = await supabase.auth.getUser();
          newUser = authUser;
        }
      }

      if (!newUser) {
        throw new Error(t("onboarding.register.errors.userNotFound"));
      }

      // 3. Esperar a que el perfil se cree (el trigger de la BD lo crea automáticamente)
      // Si no existe, crearlo manualmente
      let profile = null;
      let attempts = 0;
      const maxAttempts = 5;

      while (!profile && attempts < maxAttempts) {
        try {
          profile = await getProfile(newUser.id);
          if (profile) break;
        } catch (e) {
          // Si es un error 406 o PGRST116, el perfil probablemente no existe aún
          const errorCode = (e as any)?.code;
          if (
            errorCode === "PGRST116" ||
            (e as any)?.message?.includes("406")
          ) {
            // Esperar un poco más para que el trigger se ejecute
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }
        }
        attempts++;
      }

      // Si después de los intentos no hay perfil, crearlo manualmente
      if (!profile) {
        try {
          profile = await createProfile({
            id: newUser.id,
            full_name: registerData.fullName,
            email: registerData.email,
            user_type: null,
            onboarding_completed: false,
            country: registerData.country,
            credits_remaining: 0,
            is_active: true,
            plan: "free",
            profile_image_url: null,
          });
        } catch (createError) {
          console.error("Error creando perfil manualmente:", createError);
          // Intentar una vez más obtener el perfil después de crear
          try {
            await new Promise((resolve) => setTimeout(resolve, 1000));
            profile = await getProfile(newUser.id);
          } catch (finalError) {
            throw new Error(t("onboarding.register.errors.profileNotFound"));
          }
        }
      }

      if (!profile) {
        throw new Error(t("onboarding.register.errors.profileCreateError"));
      }

      // Update profile with selected user type and full_name from registration
      await updateProfile(newUser.id, {
        full_name: registerData.fullName,
        country: registerData.country,
        user_type: selectedType,
      });

      // Create onboarding progress (lawyer=7 steps, client=1)
      const totalSteps = selectedType === "lawyer" ? 7 : 1;
      await createOnboardingProgress(newUser.id, totalSteps);

      // 6. Forzar actualización del perfil en el contexto
      await refreshProfile();

      // 7. Esperar un momento para que el contexto se actualice
      await new Promise((resolve) => setTimeout(resolve, 500));

      // 8. Navegar directamente al onboarding específico
      toast.success(t("onboarding.register.success.accountCreated"));
      navigate(`/onboarding/${selectedType}`);
    } catch (err) {
      console.error("Registration error:", err);

      let errorMessage = t("onboarding.register.errors.accountCreateError");

      if (err instanceof Error) {
        const errMsg = err.message.toLowerCase();
        // Only show "email already registered" for actual duplicate email errors
        if (
          errMsg.includes("user already registered") ||
          errMsg.includes("already been registered") ||
          errMsg.includes("email already in use")
        ) {
          errorMessage = t("onboarding.register.errors.emailAlreadyRegistered");
        } else if (errMsg.includes("password")) {
          errorMessage = t("onboarding.register.errors.passwordRequirements");
        } else if (
          errMsg.includes("row-level security") ||
          errMsg.includes("42501")
        ) {
          // RLS policy error - profile creation failed
          errorMessage = t("onboarding.register.errors.profileCreateError");
        } else {
          errorMessage = err.message;
        }
      }

      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = async () => {
    // Validar que se haya seleccionado un tipo
    if (!selectedType) {
      toast.error(t("onboarding.register.errors.selectUserType"));
      return;
    }

    // Obtener el usuario actual (puede no estar en el contexto aún)
    let currentUser = user;
    if (!currentUser) {
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();
      currentUser = authUser;
    }

    if (!currentUser) {
      toast.error(t("onboarding.register.errors.userInfoError"));
      return;
    }

    setLoading(true);
    try {
      // Update profile with selected user type
      const { error: profileError } = await supabase
        .from("profiles")
        .update({ user_type: selectedType })
        .eq("id", currentUser.id);

      if (profileError) {
        console.error("Profile update error:", profileError);
        throw profileError;
      }

      // Create onboarding progress (lawyer=7 steps, client=1)
      const totalSteps = selectedType === "lawyer" ? 7 : 1;
      await createOnboardingProgress(currentUser.id, totalSteps);

      // Refresh profile to get updated user_type
      await refreshProfile();

      // Navigate to specific onboarding page

      navigate(`/onboarding/${selectedType}`);
    } catch (error) {
      console.error("Error setting user type:", error);

      let errorMessage = t("onboarding.register.errors.profileSetupError");
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (
        typeof error === "object" &&
        error !== null &&
        "message" in error
      ) {
        errorMessage = String(error.message);
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Render user type selection step (first step if no user)
  if (step === "select-type" && !user) {
    return (
      <div className="relative min-h-screen bg-linear-to-b from-romance-50 via-white to-romance-50 flex items-center justify-center p-4 overflow-hidden">
        {/* Animated background lines (light theme) */}
        <div className="absolute inset-0 opacity-15 overflow-hidden pointer-events-none">
          <div className="absolute top-1/5 left-0 w-full h-px bg-linear-to-r from-transparent via-tiber-500/40 to-transparent animate-pulse" />
          <div className="absolute top-2/5 left-0 w-full h-px bg-linear-to-r from-transparent via-tiber-600/30 to-transparent animate-pulse" style={{ animationDelay: "0.8s" }} />
          <div className="absolute top-3/5 left-0 w-full h-px bg-linear-to-r from-transparent via-tiber-500/35 to-transparent animate-pulse" style={{ animationDelay: "1.6s" }} />
          <div className="absolute top-4/5 left-0 w-full h-px bg-linear-to-r from-transparent via-tiber-600/25 to-transparent animate-pulse" style={{ animationDelay: "2.4s" }} />
          <div className="absolute inset-0">
            <div className="absolute top-1/3 left-0 w-[200%] h-px bg-linear-to-r from-transparent via-tiber-600/25 to-transparent rotate-12 origin-left" style={{ animation: "slide 18s linear infinite" }} />
            <div className="absolute top-2/3 left-0 w-[200%] h-px bg-linear-to-r from-transparent via-tiber-500/30 to-transparent -rotate-12 origin-left" style={{ animation: "slide-reverse 22s linear infinite", animationDelay: "3s" }} />
          </div>
          <div className="absolute top-0 left-1/5 w-px h-full bg-linear-to-b from-transparent via-tiber-600/20 to-transparent animate-pulse" style={{ animationDelay: "0.3s" }} />
          <div className="absolute top-0 left-1/2 w-px h-full bg-linear-to-b from-transparent via-tiber-500/25 to-transparent animate-pulse" style={{ animationDelay: "1.2s" }} />
          <div className="absolute top-0 left-4/5 w-px h-full bg-linear-to-b from-transparent via-tiber-600/20 to-transparent animate-pulse" style={{ animationDelay: "1.8s" }} />
        </div>
        <div className="relative z-10 max-w-6xl w-full">
          {/* Header: Logo arriba, título abajo */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <img
              src="/images/brand/lawgentia-logo.png"
              alt="Lawgentia"
              className="w-20 h-20 mx-auto mb-6"
            />
            <h1 className="text-4xl md:text-5xl font-bold text-tiber-900 mb-4">
              {t("onboarding.selectType.title")}
            </h1>
            <p className="text-xl font-medium text-tiber-800">
              {t("onboarding.selectType.subtitle")}
            </p>
          </motion.div>

          {/* User Type Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 mb-8 max-w-4xl mx-auto">
            {userTypes.map((userType, index) => {
              const Icon = userType.icon;
              const isSelected = selectedType === userType.type;

              return (
                <motion.div
                  key={userType.type}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => setSelectedType(userType.type)}
                  className={`relative bg-white rounded-2xl p-8 cursor-pointer transition-all duration-300 border-2 ${
                    isSelected
                      ? "border-tiber-600 ring-4 ring-tiber-600/30 shadow-2xl shadow-tiber-900/10 scale-[1.02]"
                      : "border-transparent hover:shadow-xl hover:border-tiber-200 hover:scale-[1.01]"
                  }`}
                >
                  {/* Icon */}
                  <div
                    className={`${userType.color} w-16 h-16 rounded-xl flex items-center justify-center mb-4`}
                  >
                    <Icon className="w-8 h-8 text-white" />
                  </div>

                  {/* Title & Description */}
                  <h3 className="text-2xl font-bold text-tiber-900 mb-2">
                    {userType.title}
                  </h3>
                  <p className="text-tiber-800 mb-6">{userType.description}</p>

                  {/* Features */}
                  <ul className="space-y-3">
                    {userType.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <svg
                          className="w-5 h-5 text-tiber-600 mr-2 mt-0.5 shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="text-sm text-tiber-800">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* Selected Indicator */}
                  {isSelected && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute top-4 right-4"
                    >
                      <div className="bg-tiber-600 rounded-full p-1">
                        <svg
                          className="w-6 h-6 text-white"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* Continue Button */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="flex justify-center"
          >
            <Button
              onClick={handleTypeSelection}
              disabled={!selectedType || loading}
              variant="secondary"
              size="md"
              isLoading={loading}
              className="w-full max-w-4xl gap-2"
            >
              {t("onboarding.continue")} <ArrowRightIcon className="w-4 h-4" />
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  // Render registration step (after type selection)
  if (step === "register") {
    return (
      <div className="relative min-h-screen bg-gradient-hero flex items-center justify-center p-4 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-tiber-500/20 blur-3xl" />
          <div className="absolute bottom-1/4 -left-32 w-80 h-80 rounded-full bg-tiber-400/15 blur-3xl" />
        </div>
        <div className="absolute inset-0 opacity-20 overflow-hidden pointer-events-none">
          <div className="absolute top-1/5 left-0 w-full h-px bg-linear-to-r from-transparent via-white/40 to-transparent animate-pulse" />
          <div className="absolute top-2/5 left-0 w-full h-px bg-linear-to-r from-transparent via-white/30 to-transparent animate-pulse" style={{ animationDelay: "0.8s" }} />
          <div className="absolute top-3/5 left-0 w-full h-px bg-linear-to-r from-transparent via-white/35 to-transparent animate-pulse" style={{ animationDelay: "1.6s" }} />
          <div className="absolute top-4/5 left-0 w-full h-px bg-linear-to-r from-transparent via-white/25 to-transparent animate-pulse" style={{ animationDelay: "2.4s" }} />
          <div className="absolute inset-0">
            <div className="absolute top-1/3 left-0 w-[200%] h-px bg-linear-to-r from-transparent via-white/25 to-transparent rotate-12 origin-left" style={{ animation: "slide 18s linear infinite" }} />
            <div className="absolute top-2/3 left-0 w-[200%] h-px bg-linear-to-r from-transparent via-white/30 to-transparent -rotate-12 origin-left" style={{ animation: "slide-reverse 22s linear infinite", animationDelay: "3s" }} />
          </div>
          <div className="absolute top-0 left-1/5 w-px h-full bg-linear-to-b from-transparent via-white/20 to-transparent animate-pulse" style={{ animationDelay: "0.3s" }} />
          <div className="absolute top-0 left-1/2 w-px h-full bg-linear-to-b from-transparent via-white/25 to-transparent animate-pulse" style={{ animationDelay: "1.2s" }} />
          <div className="absolute top-0 left-4/5 w-px h-full bg-linear-to-b from-transparent via-white/20 to-transparent animate-pulse" style={{ animationDelay: "1.8s" }} />
        </div>
        <div className="relative z-10 w-full max-w-lg">
          <div className="bg-white rounded-lg shadow-lg p-8">
            {/* Header: Logo/icono arriba, título abajo */}
            <div className="text-center mb-8">
              {selectedType &&
                (() => {
                  const UserTypeIcon =
                    userTypes.find((t) => t.type === selectedType)?.icon ||
                    UserIcon;
                  return (
                    <div
                      className={`${userTypes.find((t) => t.type === selectedType)?.color || "bg-linear-to-br from-tiber-500 to-tiber-700"} w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4`}
                    >
                      <UserTypeIcon className="w-8 h-8 text-white" />
                    </div>
                  );
                })()}
              <h2 className="text-3xl font-bold text-juridico mb-2">
                {selectedType === "lawyer"
                  ? t("onboarding.register.lawyer.title")
                  : t("onboarding.register.client.title")}
              </h2>
              <p className="text-titan">{t("onboarding.register.subtitle")}</p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Form */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleRegister();
              }}
              className="space-y-4"
            >
              {/* Full Name */}
              <Input
                type="text"
                placeholder={t("onboarding.register.fullNamePlaceholder")}
                value={registerData.fullName}
                onChange={(e) =>
                  setRegisterData({ ...registerData, fullName: e.target.value })
                }
                leftIcon={<UserIcon className="h-5 w-5" />}
                required
              />

              {/* Email */}
              <Input
                type="email"
                placeholder={t("onboarding.register.emailPlaceholder")}
                value={registerData.email}
                onChange={(e) =>
                  setRegisterData({ ...registerData, email: e.target.value })
                }
                leftIcon={<EnvelopeIcon className="h-5 w-5" />}
                required
              />

              {/* Password */}
              <Input
                type="password"
                placeholder={t("onboarding.register.passwordPlaceholder")}
                value={registerData.password}
                onChange={(e) =>
                  setRegisterData({ ...registerData, password: e.target.value })
                }
                leftIcon={<LockClosedIcon className="h-5 w-5" />}
                isPasswordVisible={isPasswordVisible}
                onTogglePasswordVisibility={() =>
                  setIsPasswordVisible(!isPasswordVisible)
                }
                required
              />

              {/* Confirm Password */}
              <Input
                type="password"
                placeholder={t(
                  "onboarding.register.confirmPasswordPlaceholder",
                )}
                value={registerData.confirmPassword}
                onChange={(e) =>
                  setRegisterData({
                    ...registerData,
                    confirmPassword: e.target.value,
                  })
                }
                leftIcon={<LockClosedIcon className="h-5 w-5" />}
                isPasswordVisible={isConfirmPasswordVisible}
                onTogglePasswordVisibility={() =>
                  setIsConfirmPasswordVisible(!isConfirmPasswordVisible)
                }
                required
              />

              {/* Country - always from DB */}
              <div className="relative">
                <GlobeAltIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-titan" />
                <select
                  value={registerData.country}
                  onChange={async (e) => {
                    const selectedCountry = e.target.value;
                    if (
                      (await isCountryEnabled(selectedCountry)) ||
                      selectedCountry === ""
                    ) {
                      setRegisterData({
                        ...registerData,
                        country: selectedCountry,
                      });
                    } else {
                      toast.error(
                        t("onboarding.register.errors.countryNotEnabled"),
                      );
                    }
                  }}
                  disabled={countriesLoading}
                  className="w-full pl-10 pr-4 py-3 border border-tiber-300 rounded-lg focus:ring-2 focus:ring-tiber-500 focus:border-transparent appearance-none bg-white disabled:opacity-70"
                  required
                >
                  <option value="">
                    {countriesLoading
                      ? t("common.loading")
                      : t("onboarding.register.countryPlaceholder")}
                  </option>
                  {countries.map((country) => {
                    const enabled = country.enabled;
                    const countryName =
                      i18n.language === "es" ? country.nameEs : country.nameEn;
                    return (
                      <option
                        key={country.code}
                        value={country.code}
                        disabled={!enabled}
                        className={!enabled ? "opacity-50" : ""}
                      >
                        {countryName}
                        {!enabled && ` (${t("common.comingSoon")})`}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Terms and Conditions Checkbox */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="acceptedTerms"
                  checked={registerData.acceptedTerms}
                  onChange={(e) =>
                    setRegisterData({
                      ...registerData,
                      acceptedTerms: e.target.checked,
                    })
                  }
                  className="mt-1 h-4 w-4 text-tiber-500 border-tiber-300 rounded focus:ring-tiber-500"
                />
                <label
                  htmlFor="acceptedTerms"
                  className="text-sm text-tiber-700"
                >
                  {t("onboarding.register.acceptTerms")}{" "}
                  <a
                    href="/legal/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-tiber-500 hover:underline font-medium"
                  >
                    {t("onboarding.register.termsLink")}
                  </a>{" "}
                  {t("onboarding.register.and")}{" "}
                  <a
                    href="/legal/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-tiber-500 hover:underline font-medium"
                  >
                    {t("onboarding.register.privacyLink")}
                  </a>
                </label>
              </div>

              {/* Back Button */}
              <Button
                type="button"
                variant="ghost"
                onClick={() => setStep("select-type")}
                className="w-full mb-2"
                size="md"
                disabled={loading}
              >
                {t("common.back")}
              </Button>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full"
                size="lg"
                isLoading={loading}
              >
                {t("onboarding.register.createAccount")}
              </Button>
            </form>

            {/* Login Link */}
            <p className="text-center mt-6 text-sm text-titan">
              {t("onboarding.register.alreadyHaveAccount")}{" "}
              <button
                type="button"
                onClick={() => navigate("/auth")}
                className="text-tiber-500 hover:underline font-medium"
              >
                {t("onboarding.register.signIn")}
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Render user type selection step (for logged in users without type)
  return (
    <div className="relative min-h-screen bg-linear-to-b from-romance-50 via-white to-romance-50 flex items-center justify-center p-4 overflow-hidden">
      {/* Animated background lines (light theme) */}
      <div className="absolute inset-0 opacity-15 overflow-hidden pointer-events-none">
        <div className="absolute top-1/5 left-0 w-full h-px bg-linear-to-r from-transparent via-tiber-500/40 to-transparent animate-pulse" />
        <div className="absolute top-2/5 left-0 w-full h-px bg-linear-to-r from-transparent via-tiber-600/30 to-transparent animate-pulse" style={{ animationDelay: "0.8s" }} />
        <div className="absolute top-3/5 left-0 w-full h-px bg-linear-to-r from-transparent via-tiber-500/35 to-transparent animate-pulse" style={{ animationDelay: "1.6s" }} />
        <div className="absolute top-4/5 left-0 w-full h-px bg-linear-to-r from-transparent via-tiber-600/25 to-transparent animate-pulse" style={{ animationDelay: "2.4s" }} />
        <div className="absolute inset-0">
          <div className="absolute top-1/3 left-0 w-[200%] h-px bg-linear-to-r from-transparent via-tiber-600/25 to-transparent rotate-12 origin-left" style={{ animation: "slide 18s linear infinite" }} />
          <div className="absolute top-2/3 left-0 w-[200%] h-px bg-linear-to-r from-transparent via-tiber-500/30 to-transparent -rotate-12 origin-left" style={{ animation: "slide-reverse 22s linear infinite", animationDelay: "3s" }} />
        </div>
        <div className="absolute top-0 left-1/5 w-px h-full bg-linear-to-b from-transparent via-tiber-600/20 to-transparent animate-pulse" style={{ animationDelay: "0.3s" }} />
        <div className="absolute top-0 left-1/2 w-px h-full bg-linear-to-b from-transparent via-tiber-500/25 to-transparent animate-pulse" style={{ animationDelay: "1.2s" }} />
        <div className="absolute top-0 left-4/5 w-px h-full bg-linear-to-b from-transparent via-tiber-600/20 to-transparent animate-pulse" style={{ animationDelay: "1.8s" }} />
      </div>
      <div className="relative z-10 max-w-6xl w-full">
        {/* Header: Logo arriba, título abajo */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <img
            src="/images/brand/lawgentia-logo.png"
            alt="Lawgentia"
            className="w-20 h-20 mx-auto mb-6"
          />
          <h1 className="text-4xl md:text-5xl font-bold text-tiber-900 mb-4">
            {t("onboarding.selectType.title")}
          </h1>
          <p className="text-xl font-medium text-tiber-800">
            {t("onboarding.selectType.subtitle")}
          </p>
        </motion.div>

        {/* User Type Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 mb-8 max-w-4xl mx-auto">
          {userTypes.map((userType, index) => {
            const Icon = userType.icon;
            const isSelected = selectedType === userType.type;

            return (
              <motion.div
                key={userType.type}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => setSelectedType(userType.type)}
                className={`relative bg-white rounded-2xl p-8 cursor-pointer transition-all duration-300 border-2 ${
                  isSelected
                    ? "border-tiber-600 ring-4 ring-tiber-600/30 shadow-2xl shadow-tiber-900/10 scale-[1.02]"
                    : "border-transparent hover:shadow-xl hover:border-tiber-200 hover:scale-[1.01]"
                }`}
              >
                {/* Icon */}
                <div
                  className={`${userType.color} w-16 h-16 rounded-xl flex items-center justify-center mb-4`}
                >
                  <Icon className="w-8 h-8 text-white" />
                </div>

                {/* Title & Description */}
                <h3 className="text-2xl font-bold text-tiber-900 mb-2">
                  {userType.title}
                </h3>
                <p className="text-tiber-800 mb-6">{userType.description}</p>

                {/* Features */}
                <ul className="space-y-3">
                  {userType.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <svg
                        className="w-5 h-5 text-tiber-600 mr-2 mt-0.5 shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-sm text-tiber-800">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Selected Indicator */}
                {isSelected && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute top-4 right-4"
                  >
                    <div className="bg-tiber-600 rounded-full p-1">
                      <svg
                        className="w-6 h-6 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Continue Button */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-center"
        >
          <Button
            onClick={handleContinue}
            disabled={!selectedType || loading}
            variant="secondary"
            size="md"
            fullWidth
            isLoading={loading}
            className="gap-2"
          >
            {t("onboarding.continue")} <ArrowRightIcon className="w-4 h-4" />
          </Button>
        </motion.div>
      </div>
    </div>
  );
};
